import { useCallback, useState } from 'react';
import { dealSeats, type LocalSeat } from '@bm/engine';
import { de } from '@/i18n/de';
import { haptics } from '@/lib/haptics';
import { browserRng } from '@/lib/rng';
import { pushBackGuard, useBackGuard } from '@/lib/backGuard';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { useWakeLock } from '@/lib/wakeLock';
import { loadLocalSetup, saveLocalSetup, type LocalRoundSetup } from '@/lib/localRound';
import { LocalSetupScreen } from './LocalSetupScreen';
import { LocalHandoffScreen } from './LocalHandoffScreen';
import { LocalRevealScreen } from './LocalRevealScreen';
import { LocalDoneScreen } from './LocalDoneScreen';

type Step =
  | { kind: 'SETUP' }
  | { kind: 'HANDOFF'; index: number }
  | { kind: 'REVEAL'; index: number }
  | { kind: 'DONE' };

interface LocalModeProps {
  onExit: () => void;
}

/**
 * Ein-Gerät-Modus: die App verteilt nur die Rollen, gespielt wird offline.
 *
 * Bewusst vollständig lokal — kein Raum, kein Server, keine Verbindung nötig.
 * Der Schrittzustand lebt nur in diesem Teilbaum (nicht im globalen Store), und
 * die verteilte Runde liegt ausschließlich im Arbeitsspeicher: Ein Reload
 * beginnt neu, statt die Rollen im `localStorage` lesbar zu hinterlassen.
 */
export function LocalMode({ onExit }: LocalModeProps) {
  const [setup, setSetup] = useState<LocalRoundSetup>(loadLocalSetup);
  /** Übergabereihenfolge samt Rollen — bei jeder Verteilung neu gemischt. */
  const [seats, setSeats] = useState<LocalSeat[]>([]);
  const [step, setStep] = useState<Step>({ kind: 'SETUP' });
  const [abortOpen, setAbortOpen] = useState(false);

  const inRound = step.kind !== 'SETUP';
  useWakeLock(step.kind === 'HANDOFF' || step.kind === 'REVEAL');
  useBackGuard(
    inRound,
    useCallback(() => setAbortOpen(true), []),
  );

  const updateSetup = useCallback((next: LocalRoundSetup) => {
    setSetup(next);
    saveLocalSetup(next);
  }, []);

  const deal = useCallback(() => {
    setSeats(dealSeats(setup.names, setup.mafiaCount, browserRng));
    setStep({ kind: 'HANDOFF', index: 0 });
    haptics.phaseChange();
  }, [setup]);

  const advance = useCallback(
    (index: number) => {
      const next = index + 1;
      setStep(next >= seats.length ? { kind: 'DONE' } : { kind: 'HANDOFF', index: next });
      haptics.tick();
    },
    [seats.length],
  );

  return (
    <>
      {renderStep()}
      <ConfirmSheet
        open={abortOpen}
        title={de.local.abortConfirm}
        confirmLabel={de.local.abortConfirmCta}
        destructive
        onConfirm={onExit}
        onCancel={() => {
          setAbortOpen(false);
          // Der Zurück-Tap hat den reservierten History-Eintrag verbraucht.
          pushBackGuard();
        }}
      />
    </>
  );

  function renderStep() {
    switch (step.kind) {
      case 'SETUP':
        return (
          <LocalSetupScreen setup={setup} onChange={updateSetup} onDeal={deal} onBack={onExit} />
        );

      case 'HANDOFF': {
        const seat = seats[step.index];
        if (!seat) return null;
        return (
          <LocalHandoffScreen
            name={seat.name}
            position={step.index + 1}
            total={seats.length}
            onConfirm={() => setStep({ kind: 'REVEAL', index: step.index })}
          />
        );
      }

      case 'REVEAL': {
        const seat = seats[step.index];
        if (!seat) return null;
        const colleagueNames = seats
          .filter((other, i) => i !== step.index && other.role === 'MAFIA')
          .map((other) => other.name);
        return (
          <LocalRevealScreen
            name={seat.name}
            role={seat.role}
            colleagueNames={seat.role === 'MAFIA' ? colleagueNames : []}
            onSeen={() => advance(step.index)}
          />
        );
      }

      case 'DONE':
        return (
          <LocalDoneScreen
            playerCount={setup.names.length}
            mafiaCount={setup.mafiaCount}
            onRedeal={deal}
            onExit={onExit}
          />
        );
    }
  }
}
